import { useState, useEffect, useCallback } from 'react';
import { useAdminStore } from '@/stores/adminStore';
import { STORAGE_KEYS } from '@/utils/constants';
import {
  Brain,
  Users,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Trash2,
  ChevronDown,
  ChevronUp,
  X,
  RefreshCw,
  BookOpen,
  Target,
  Search,
  Clock,
  Star,
  TrendingUp,
  Award
} from 'lucide-react';
import type { LearningUserData } from '@/types/admin';

interface FilterLearningSummary {
  total_critical_filters: number;
  total_corrections: number;
  critical_by_type: string[];
}

interface ReformulationRule {
  pattern: string;
  confidence: number;
  occurrences: number;
  reason: string;
}

interface ReformulationSummary {
  total_rules: number;
  rules: ReformulationRule[];
}

interface UserDetails {
  user_id: string;
  synonyms: {
    total_synonyms: number;
    verified_synonyms: Array<{ term: string; canonical: string; confidence: number }>;
    pending_synonyms: string[];
    rejected_synonyms: string[];
  };
  patterns: Array<{
    user_query: string;
    interpreted_intent: string;
    extracted_filters: Record<string, any>;
    timestamp: string;
    confidence: number;
  }>;
  feedback_history: Array<{
    timestamp: string;
    query: string;
    feedback_type: string;
    is_positive?: boolean;
    correction?: string;
    metadata?: Record<string, any>;
  }>;
  context_patterns: Record<string, any>;
  filter_learning: FilterLearningSummary;
  reformulations: ReformulationSummary;
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ElementType;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

function StatCard({ label, value, icon: Icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-accent-blue/10 text-accent-blue border-accent-blue/20',
    green: 'bg-accent-green/10 text-accent-green border-accent-green/20',
    purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white/50`}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs opacity-80">{label}</div>
        </div>
      </div>
    </div>
  );
}

interface UserDetailModalProps {
  userId: string | null;
  username: string;
  onClose: () => void;
}

function UserDetailModal({ userId, username, onClose }: UserDetailModalProps) {
  const [details, setDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'synonyms' | 'feedback' | 'patterns' | 'filters' | 'reformulations' | 'preferences'>('synonyms');

  useEffect(() => {
    if (!userId) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const response = await fetch(`/api/learning/admin/user/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.status === 401) {
          localStorage.removeItem(STORAGE_KEYS.TOKEN);
          window.location.href = '/login';
          return;
        }
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}`);
        }

        const data = await response.json();
        setDetails(data);
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [userId]);

  if (!userId) return null;

  const tabs = [
    { id: 'synonyms', label: 'Sinónimos', icon: BookOpen },
    { id: 'feedback', label: 'Feedback', icon: ThumbsUp },
    { id: 'patterns', label: 'Patrones', icon: TrendingUp },
    { id: 'filters', label: 'Filtros Críticos', icon: Target },
    { id: 'reformulations', label: 'Reformulaciones', icon: RefreshCw },
    { id: 'preferences', label: 'Preferencias', icon: Star },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4">
      <div className="bg-bg-secondary rounded-xl border border-border-subtle w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
          <div>
            <h3 className="text-lg font-bold text-text-primary">{username}</h3>
            <p className="text-xs text-text-muted">ID: {userId}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-bg-tertiary flex items-center justify-center text-text-muted"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-border-subtle px-4 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id
                      ? 'border-accent-blue text-accent-blue'
                      : 'border-transparent text-text-muted hover:text-text-secondary'
                    }`}
                >
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-text-muted">
                <RefreshCw size={20} className="animate-spin mr-2" />
                Cargando...
              </div>
            ) : !details ? (
              <div className="text-center py-12 text-text-muted">
                No se pudieron cargar los detalles
              </div>
            ) : (
              <>
                {activeTab === 'synonyms' && (
                  <div className="space-y-6">
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-accent-green/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-accent-green">{details.synonyms.verified_synonyms.length}</div>
                        <div className="text-xs text-text-muted">Verificados</div>
                      </div>
                      <div className="p-4 bg-orange-500/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-orange-500">{details.synonyms.pending_synonyms.length}</div>
                        <div className="text-xs text-text-muted">Pendientes</div>
                      </div>
                      <div className="p-4 bg-accent-red/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-accent-red">{details.synonyms.rejected_synonyms.length}</div>
                        <div className="text-xs text-text-muted">Rechazados</div>
                      </div>
                    </div>

                    {/* Verified Synonyms */}
                    {details.synonyms.verified_synonyms.length > 0 && (
                      <div>
                        <h4 className="font-medium text-text-secondary mb-3 flex items-center gap-2">
                          <Award size={16} className="text-accent-green" />
                          Sinónimos Verificados
                        </h4>
                        <div className="space-y-2">
                          {details.synonyms.verified_synonyms.map((syn, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-bg-tertiary rounded-lg">
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-1 bg-accent-blue/10 text-accent-blue text-xs rounded font-medium">
                                  {syn.term}
                                </span>
                                <span className="text-text-muted">→</span>
                                <span className="text-text-secondary">{syn.canonical}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-20 h-2 bg-bg-secondary rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-accent-green rounded-full"
                                    style={{ width: `${syn.confidence * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs text-text-muted w-10 text-right">
                                  {(syn.confidence * 100).toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pending */}
                    {details.synonyms.pending_synonyms.length > 0 && (
                      <div>
                        <h4 className="font-medium text-text-secondary mb-3 flex items-center gap-2">
                          <Clock size={16} className="text-orange-500" />
                          Pendientes de Verificación
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {details.synonyms.pending_synonyms.map((syn, idx) => (
                            <span key={idx} className="px-3 py-1.5 bg-orange-500/10 text-orange-500 text-xs rounded-full">
                              {syn}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'feedback' && (
                  <div className="space-y-6">
                    {details.feedback_history.length === 0 ? (
                      <div className="text-center py-12 text-text-muted">
                        <MessageSquare size={48} className="mx-auto mb-4 opacity-20" />
                        No hay feedback registrado
                      </div>
                    ) : (
                      // Agrupar feedbacks por query
                      Object.entries(
                        details.feedback_history.reduce((acc, fb) => {
                          const query = fb.query || 'Sin consulta';
                          if (!acc[query]) acc[query] = [];
                          acc[query].push(fb);
                          return acc;
                        }, {} as Record<string, typeof details.feedback_history>)
                      ).map(([query, feedbacks], groupIdx) => (
                        <div key={groupIdx} className="bg-bg-tertiary rounded-lg border border-border-subtle overflow-hidden">
                          {/* Query header */}
                          <div className="px-4 py-3 bg-bg-secondary border-b border-border-subtle">
                            <div className="text-sm font-medium text-text-primary truncate" title={query}>
                              "{query}"
                            </div>
                            <div className="text-xs text-text-muted mt-1">
                              {feedbacks.length} evento{feedbacks.length > 1 ? 's' : ''} registrado{feedbacks.length > 1 ? 's' : ''}
                            </div>
                          </div>

                          {/* Feedbacks list */}
                          <div className="divide-y divide-border-subtle">
                            {feedbacks.map((fb, idx) => (
                              <div key={idx} className="px-4 py-3 flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${fb.is_positive === true ? 'bg-accent-green/10 text-accent-green' :
                                    fb.is_positive === false ? 'bg-accent-red/10 text-accent-red' :
                                      'bg-orange-500/10 text-orange-500'
                                  }`}>
                                  {fb.is_positive === true ? <ThumbsUp size={14} /> :
                                    fb.is_positive === false ? <ThumbsDown size={14} /> :
                                      <MessageSquare size={14} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-text-muted">
                                      {(() => {
                                        try {
                                          const ts = parseFloat(fb.timestamp) * 1000;
                                          if (isNaN(ts)) return 'Fecha inválida';
                                          return new Date(ts).toLocaleString('es-MX');
                                        } catch {
                                          return 'Fecha inválida';
                                        }
                                      })()}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded ${fb.feedback_type?.includes('explicit') ? 'bg-accent-blue/10 text-accent-blue' :
                                        'bg-orange-500/10 text-orange-500'
                                      }`}>
                                      {fb.feedback_type}
                                    </span>
                                  </div>
                                  {fb.correction && (
                                    <div className="mt-1 p-2 bg-accent-red/5 border border-accent-red/20 rounded text-sm">
                                      <span className="text-accent-red font-medium">Corrección:</span> {fb.correction}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'patterns' && (
                  <div className="space-y-4">
                    {details.patterns.length === 0 ? (
                      <div className="text-center py-12 text-text-muted">
                        <TrendingUp size={48} className="mx-auto mb-4 opacity-20" />
                        No hay patrones almacenados
                      </div>
                    ) : (
                      details.patterns.map((pattern, idx) => (
                        <div key={idx} className="p-4 bg-bg-tertiary rounded-lg border border-border-subtle">
                          <div className="flex items-start justify-between mb-2">
                            <div className="font-medium text-text-primary">"{pattern.user_query}"</div>
                            <span className={`text-xs px-2 py-0.5 rounded ${pattern.confidence > 0.8 ? 'bg-accent-green/10 text-accent-green' :
                                pattern.confidence > 0.5 ? 'bg-orange-500/10 text-orange-500' :
                                  'bg-accent-red/10 text-accent-red'
                              }`}>
                              {(pattern.confidence * 100).toFixed(0)}% confianza
                            </span>
                          </div>
                          <div className="text-sm text-text-muted mb-2">
                            Intent detectado: <span className="text-accent-blue">{pattern.interpreted_intent}</span>
                          </div>
                          {Object.keys(pattern.extracted_filters).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {Object.entries(pattern.extracted_filters).map(([key, value]) => (
                                <span key={key} className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-muted">
                                  {key}: {String(value)}
                                </span>
                              ))}
                            </div>
                          )}
                          {pattern.timestamp && (
                            <div className="text-xs text-text-muted mt-2">
                              {new Date(pattern.timestamp).toLocaleString('es-MX')}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'filters' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-4 bg-accent-blue/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-accent-blue">{details.filter_learning?.total_critical_filters || 0}</div>
                        <div className="text-xs text-text-muted">Filtros Críticos</div>
                      </div>
                      <div className="p-4 bg-orange-500/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-orange-500">{details.filter_learning?.total_corrections || 0}</div>
                        <div className="text-xs text-text-muted">Correcciones</div>
                      </div>
                      <div className="p-4 bg-purple-500/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-500">{details.filter_learning?.critical_by_type?.length || 0}</div>
                        <div className="text-xs text-text-muted">Tipos Críticos</div>
                      </div>
                    </div>

                    {details.filter_learning?.critical_by_type?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-text-secondary mb-3">Tipos de Filtros Críticos</h4>
                        <div className="flex flex-wrap gap-2">
                          {details.filter_learning.critical_by_type.map((type, idx) => (
                            <span key={idx} className="px-3 py-1.5 bg-accent-red/10 text-accent-red text-xs rounded-full font-medium">
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!details.filter_learning?.total_critical_filters && !details.filter_learning?.total_corrections) && (
                      <div className="text-center py-12 text-text-muted">
                        <Target size={48} className="mx-auto mb-4 opacity-20" />
                        <p className="text-sm">No hay filtros críticos registrados aún.</p>
                        <p className="text-xs mt-2 opacity-70">El sistema aprenderá cuando el usuario proporcione feedback negativo con correcciones.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'reformulations' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-accent-green/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-accent-green">{details.reformulations?.total_rules || 0}</div>
                        <div className="text-xs text-text-muted">Reglas de Reformulación</div>
                      </div>
                      <div className="p-4 bg-accent-blue/10 rounded-lg text-center">
                        <div className="text-2xl font-bold text-accent-blue">
                          {details.reformulations?.rules?.reduce((acc, r) => acc + r.occurrences, 0) || 0}
                        </div>
                        <div className="text-xs text-text-muted">Aplicaciones</div>
                      </div>
                    </div>

                    {details.reformulations?.rules?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-text-secondary mb-3">Reglas Aprendidas</h4>
                        <div className="space-y-2">
                          {details.reformulations.rules.map((rule, idx) => (
                            <div key={idx} className="p-3 bg-bg-tertiary rounded-lg border border-border-subtle">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-text-muted">Patrón:</span>
                                <span className="text-xs px-2 py-0.5 bg-accent-blue/10 text-accent-blue rounded">
                                  {rule.confidence > 0.7 ? 'Alta confianza' : 'En aprendizaje'}
                                </span>
                              </div>
                              <div className="font-medium text-text-primary mb-2">"{rule.pattern}"</div>
                              <div className="flex items-center gap-4 text-xs text-text-muted">
                                <span>Usos: {rule.occurrences}</span>
                                <span>Confianza: {(rule.confidence * 100).toFixed(0)}%</span>
                              </div>
                              <div className="mt-2 text-xs text-orange-500">{rule.reason}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!details.reformulations?.total_rules) && (
                      <div className="text-center py-12 text-text-muted">
                        <RefreshCw size={48} className="mx-auto mb-4 opacity-20" />
                        <p className="text-sm">No hay reglas de reformulación aún.</p>
                        <p className="text-xs mt-2 opacity-70">El sistema aprenderá a reformular queries similares basándose en correcciones previas.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'preferences' && (
                  <div className="space-y-6">
                    {Object.keys(details.context_patterns).length === 0 ? (
                      <div className="text-center py-12 text-text-muted">
                        <Star size={48} className="mx-auto mb-4 opacity-20" />
                        No hay preferencias registradas
                      </div>
                    ) : (
                      Object.entries(details.context_patterns).map(([key, value]) => (
                        <div key={key} className="p-4 bg-bg-tertiary rounded-lg">
                          <h4 className="font-medium text-text-secondary mb-2 capitalize">{key.replace(/_/g, ' ')}</h4>
                          <div className="text-sm text-text-primary">
                            {typeof value === 'object' ? (
                              <pre className="text-xs bg-bg-secondary p-2 rounded overflow-x-auto">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : (
                              String(value)
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface UserRowProps {
  user: LearningUserData;
  onViewDetails: (userId: string, username: string) => void;
  onDelete: (userId: string) => void;
}

function UserRow({ user, onViewDetails, onDelete }: UserRowProps) {
  const feedbackRatio = user.feedback_total > 0
    ? Math.round((user.feedback_positive / user.feedback_total) * 100)
    : 0;

  return (
    <div className="p-4 bg-bg-tertiary rounded-xl border border-border-subtle hover:border-accent-blue/30 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-accent-blue/10 flex items-center justify-center">
            <Users size={18} className="text-accent-blue" />
          </div>
          <div>
            <div className="font-medium text-text-primary">{user.username || `Usuario ${user.user_id}`}</div>
            <div className="text-xs text-text-muted">
              ID: {user.user_id} • {user.synonyms_learned} sinónimos • {user.patterns_stored} patrones
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onViewDetails(user.user_id, user.username || `Usuario ${user.user_id}`)}
            className="px-3 py-1.5 text-xs font-medium bg-accent-blue/10 text-accent-blue rounded-lg hover:bg-accent-blue/20 transition-colors flex items-center gap-1"
          >
            <Search size={12} />
            Ver detalles
          </button>
          <button
            onClick={() => onDelete(user.user_id)}
            className="p-1.5 text-accent-red hover:bg-accent-red/10 rounded-lg transition-colors"
            title="Borrar datos de aprendizaje"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t border-border-subtle">
        <div className="text-center">
          <div className="text-lg font-bold text-text-primary">{user.synonyms_learned}</div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider">Sinónimos</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-text-primary">{user.feedback_total}</div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider">Feedback</div>
        </div>
        <div className="text-center">
          <div className={`text-lg font-bold ${feedbackRatio >= 70 ? 'text-accent-green' : feedbackRatio >= 40 ? 'text-orange-500' : 'text-accent-red'}`}>
            {feedbackRatio}%
          </div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider">Positivo</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-text-primary">{user.context_patterns}</div>
          <div className="text-[10px] text-text-muted uppercase tracking-wider">Patrones</div>
        </div>
      </div>

      {/* Synonym Examples */}
      {user.synonym_examples.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-subtle">
          <div className="text-[10px] text-text-muted uppercase tracking-wider mb-2">Sinónimos aprendidos</div>
          <div className="flex flex-wrap gap-1.5">
            {user.synonym_examples.slice(0, 5).map((syn, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-accent-green/10 text-accent-green text-[10px] rounded">
                {syn}
              </span>
            ))}
            {user.synonym_examples.length > 5 && (
              <span className="px-2 py-0.5 bg-bg-secondary text-text-muted text-[10px] rounded">
                +{user.synonym_examples.length - 5}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LearningSystemTab() {
  const learningData = useAdminStore((s) => s.learningData);
  const setLearningData = useAdminStore((s) => s.setLearningData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<{ id: string; username: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLearningData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await fetch('/api/learning/admin/dashboard', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        window.location.href = '/login';
        return;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Respuesta no JSON:', text.substring(0, 200));
        throw new Error(`Error del servidor (status ${response.status})`);
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || `Error ${response.status}`);
      }

      const data = await response.json();
      setLearningData(data);
    } catch (err) {
      console.error('Error fetching learning data:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [setLearningData]);

  useEffect(() => {
    fetchLearningData();
    const interval = setInterval(fetchLearningData, 30000);
    return () => clearInterval(interval);
  }, [fetchLearningData]);

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(`¿Estás seguro de borrar todos los datos de aprendizaje del usuario ${userId}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await fetch(`/api/learning/admin/user/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        window.location.href = '/login';
        return;
      }

      if (response.ok) {
        fetchLearningData();
      } else {
        alert('Error al borrar datos');
      }
    } catch (error) {
      alert('Error al borrar datos');
    }
  };

  const filteredUsers = learningData?.users.filter(user => {
    const username = (user.username || user.user_id || '').toLowerCase();
    const userId = (user.user_id || '').toLowerCase();
    return username.includes(searchTerm.toLowerCase()) ||
           userId.includes(searchTerm.toLowerCase());
  }) || [];

  if (loading && !learningData) {
    return (
      <div className="flex items-center justify-center py-12 text-text-muted">
        <RefreshCw size={20} className="animate-spin mr-2" />
        Cargando datos de aprendizaje...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-accent-red mb-2 font-medium">Error cargando datos</div>
        <div className="text-text-muted text-sm mb-4 max-w-xs mx-auto">{error}</div>
        <button
          onClick={fetchLearningData}
          className="px-4 py-2 bg-accent-blue text-white rounded-lg text-sm hover:bg-accent-blue/90"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!learningData) {
    return (
      <div className="text-center py-12 text-text-muted">
        No hay datos disponibles
      </div>
    );
  }

  const { summary } = learningData;

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Usuarios Activos"
          value={summary.total_users}
          icon={Users}
          color="blue"
        />
        <StatCard
          label="Sinónimos Aprendidos"
          value={summary.total_synonyms_learned}
          icon={Brain}
          color="purple"
        />
        <StatCard
          label="Feedback Recibido"
          value={summary.total_feedback_recorded}
          icon={MessageSquare}
          color="green"
        />
        <StatCard
          label="Patrones Almacenados"
          value={summary.total_patterns_stored}
          icon={Target}
          color="orange"
        />
      </div>

      {/* Search and Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Buscar usuario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-bg-tertiary border border-border-subtle rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-blue"
          />
        </div>
        <button
          onClick={fetchLearningData}
          className="p-2 text-text-muted hover:text-text-primary transition-colors"
          title="Actualizar"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-text-muted text-sm">
            {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios con datos de aprendizaje'}
          </div>
        ) : (
          filteredUsers.map((user) => (
            <UserRow
              key={user.user_id}
              user={user}
              onViewDetails={(id, name) => setSelectedUser({ id, username: name })}
              onDelete={handleDeleteUser}
            />
          ))
        )}
      </div>

      {/* Detail Modal */}
      <UserDetailModal
        userId={selectedUser?.id || null}
        username={selectedUser?.username || ''}
        onClose={() => setSelectedUser(null)}
      />
    </div>
  );
}
